import i18next from 'i18next';
import { hideNotification, showNotification } from '../../core/actions';
import {
	getActiveSubscription,
	loadAccountDetails,
	loadPaymentSources,
	reactivateAccount,
	setPaywallForcedOpen
} from '../../core/admin/account/actions';
import { State } from '../../core/reducers';
import { getSocket } from '../../core/socket';
import { i18n } from '../../i18n';
import { api } from '../../lib/api';
import { createAsyncConstants } from '../../lib/createConstants';

export const asyncConstants = createAsyncConstants(['OPEN_CHECKOUT', 'SUBSCRIPTION_CHANGED'], {
	prefix: 'UPGRADE_BUTTON'
});
let chargebeeInstance;
declare let Chargebee: any;

// Chargebee keeps the instance flagged as authenticated after a portal was closed, and then
// reuses the previous session instead of forwarding to the requested section. Logging out
// forces a fresh portal session on every open.
const createPortal = () => {
	chargebeeInstance ??= Chargebee.init({
		site: import.meta.env.MODE === 'production' ? 'relay' : 'relay-test'
	});
	chargebeeInstance.setPortalSession(() => api.get('/api/v1/chargebee/portal_session'));
	chargebeeInstance.logout();
	return chargebeeInstance.createChargebeePortal();
};

export const openPortalSession =
	(openPaymentSources: boolean) => (dispatch, getState: () => State) => {
		const { activeSubscription, paymentSources } = getState().admin.account;
		const chargebeePortalInstance = createPortal();

		if (openPaymentSources) {
			chargebeePortalInstance.openSection(
				{ sectionType: Chargebee.getPortalSections().PAYMENT_SOURCES },
				{
					paymentSourceAdd: async () => {
						await dispatch(loadPaymentSources());

						const { paymentSources } = getState().admin.account;
						if (!paymentSources || paymentSources.length === 0) {
							dispatch(
								showNotification(i18next.t('general:payment_method_invalid_or_failed'), 'error')
							);
							return;
						}

						dispatch(
							showNotification(i18next.t('general:payment_method_add_successful'), 'success')
						);
						dispatch(reactivateAccount());
						chargebeeInstance.closeAll();
						dispatch(setPaywallForcedOpen());
					},

					paymentSourceUpdate: async () => {
						await dispatch(loadPaymentSources());
						const { paymentSources } = getState().admin.account;

						if (!paymentSources || paymentSources.length === 0) {
							dispatch(
								showNotification(i18next.t('general:payment_method_invalid_or_failed'), 'error')
							);
							return;
						}

						dispatch(
							showNotification(i18next.t('general:payment_method_update_successful'), 'success')
						);
						chargebeeInstance.closeAll();
					}
				}
			);

			return;
		}

		const hasPaymentSources = paymentSources?.length > 0;
		const options = getOptionsForChargebeePortal(hasPaymentSources, dispatch);
		const forwardOptions = getForwardOptionsForChargebeePortal(
			activeSubscription?.id,
			hasPaymentSources
		);
		const close = () => {
			dispatch(getActiveSubscription());
			dispatch(loadAccountDetails());
		};

		chargebeePortalInstance.open({ ...options, close }, forwardOptions);
	};

export const openChargebeeBillingHistory = (activeSubscription) => {
	createPortal().openSection({
		sectionType: Chargebee.getPortalSections().BILLING_HISTORY,
		subscriptionId: activeSubscription?.id
	});
};

export const startListeningForSubscriptionChanges = () => (dispatch) => {
	const socket = getSocket();
	socket.emit('users.list.entered');
	socket.on('subscription.changed', (subscription) => {
		dispatch(hideNotification());
		dispatch(showNotification(i18n.t('general:changes_successful'), 'success'));
		dispatch({
			type: asyncConstants.SUBSCRIPTION_CHANGED.FULFILLED,
			payload: subscription
		});
		chargebeeInstance.closeAll();
		dispatch(getActiveSubscription());
		dispatch(loadAccountDetails());
	});
};

export const stopListeningForSubscriptionChanges = () => {
	const socket = getSocket();
	socket.emit('users.list.exited');
	socket.off('subscription.changed');
};

export const getOptionsForChargebeePortal = (hasPaymentSources, dispatch) => {
	return hasPaymentSources
		? {
				subscriptionChanged(data) {
					dispatch(showNotification(i18n.t('general:changes_processing'), 'info', true));
					dispatch({
						type: asyncConstants.SUBSCRIPTION_CHANGED.PENDING,
						payload: data.subscription.id
					});
				}
		  }
		: {
				paymentSourceUpdate(result) {
					if (result === 'error') {
						return;
					}
					dispatch(loadPaymentSources());
					dispatch(showNotification(i18next.t('general:changes_successful'), 'success'));
				}
		  };
};

export const getForwardOptionsForChargebeePortal = (subscriptionId, hasPaymentSources) => {
	return hasPaymentSources && subscriptionId
		? {
				sectionType: Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
				params: {
					subscriptionId
				}
		  }
		: {
				sectionType: Chargebee.getPortalSections().EDIT_PAYMENT_SOURCE
		  };
};
